/** Coquilles des trois espaces : public, portail professionnel, administration. Responsive (burger < 1024 px). */
import { useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { Button, Icon, Logo } from '@myron/design-system';
import { LogOut, Menu, ShieldCheck, UserRound, X } from 'lucide-react';
import { useSession } from '../../shared/auth/session';
import { filtrerNav, type EntreeNav } from '../navigation';
import { BandeauSimulation } from '../../features/simulation/PanneauSimulation';
import { navPro } from '../../features/pro/routes';
import { navProCompte } from '../../features/pro/compte/routes';
import { navAdminCommercial } from '../../features/admin/commercial/routes';
import { navAdminVentes } from '../../features/admin/ventes/routes';
import { navAdminLogistique } from '../../features/admin/logistique/routes';
import { navAdminSysteme } from '../../features/admin/systeme/routes';
import { navAdminMarges } from '../../features/admin/marges/routes';
import { navAdminAccords } from '../../features/admin/accords/routes';
import { navAdminDevis } from '../../features/admin/devis/routes';
import { navAdminLivraison } from '../../features/admin/livraison/routes';
import { navAdminSauvegarde } from '../../features/admin/sauvegarde/routes';
import { navProConditions } from '../../features/pro/conditions/routes';
import { navProFidelite } from '../../features/pro/fidelite/routes';

function Evitement() {
  return <a href="#contenu" className="my-evitement">Aller au contenu</a>;
}

function MenuCompte({ espace }: { espace: 'pro' | 'admin' | 'public' }) {
  const { session, connecte, deconnecter } = useSession();
  const nav = useNavigate();
  if (!session || !connecte) {
    return (
      <>
        <Button variante="tertiaire" onClick={() => nav('/connexion')}>Se connecter</Button>
        {espace === 'public' && <Button onClick={() => nav('/devenir-client')}>Devenir client</Button>}
      </>
    );
  }
  const espaceCible = session.utilisateur.type === 'INTERNE' ? '/admin' : '/pro';
  return (
    <>
      {espace === 'public' && <Button variante="secondaire" onClick={() => nav(espaceCible)}>Mon espace</Button>}
      <span className="my-petit my-secondaire" data-testid="utilisateur-connecte">
        <Icon icon={UserRound} /> {session.utilisateur.prenom} {session.utilisateur.nom}
        {session.entreprise ? ` · ${session.entreprise.raisonSociale}` : ''}
      </span>
      <Button variante="tertiaire" compact icone={LogOut} onClick={async () => { await deconnecter(); nav('/connexion?deconnecte=1'); }}>
        Se déconnecter
      </Button>
    </>
  );
}

function Pied() {
  return (
    <footer className="my-pied">
      <div className="my-conteneur my-pile">
        <Logo variante="creme" hauteur={28} />
        <p className="my-petit">Grossiste alimentaire B2B — boissons, épicerie, produits alimentaires. Vente à la palette, au carton selon les références.</p>
        <p className="my-legende">Maquette de démonstration — données fictives. Aucune donnée réelle n'est collectée.</p>
      </div>
    </footer>
  );
}

function EnteteHorizontal({ liens, espace, accueil }: { liens: EntreeNav[]; espace: 'pro' | 'public'; accueil: string }) {
  const [ouvert, setOuvert] = useState(false);
  const loc = useLocation();
  return (
    <header className="my-entete">
      <nav className="my-nav" aria-label={espace === 'pro' ? 'Navigation du portail professionnel' : 'Navigation principale'}>
        <Link to={accueil} className="my-nav__marque" aria-label="MYRON — accueil"><Logo /></Link>
        <button type="button" className="my-btn my-btn--tertiaire my-burger" aria-expanded={ouvert} aria-controls="liens-nav" onClick={() => setOuvert((o) => !o)}>
          <Icon icon={ouvert ? X : Menu} /> Menu
        </button>
        <ul id="liens-nav" className={`my-nav__liens my-nav__liens--repliable${ouvert ? ' ouvert' : ''}`}>
          {liens.map((l) => (
            <li key={l.vers}>
              <NavLink to={l.vers} end={l.exact} onClick={() => setOuvert(false)} aria-current={loc.pathname === l.vers ? 'page' : undefined}>
                {l.libelle}
              </NavLink>
            </li>
          ))}
          <li className="my-nav__actions"><MenuCompte espace={espace} /></li>
        </ul>
      </nav>
    </header>
  );
}

const NAV_PUBLIQUE: EntreeNav[] = [
  { libelle: 'Accueil', vers: '/', exact: true },
  { libelle: 'Catalogue', vers: '/catalogue' },
  { libelle: 'Devenir client', vers: '/devenir-client' },
  { libelle: 'Contact', vers: '/contact' },
];

export function LayoutPublic() {
  return (
    <div className="my-app">
      <Evitement />
      <BandeauSimulation />
      <EnteteHorizontal liens={NAV_PUBLIQUE} espace="public" accueil="/" />
      <main id="contenu" className="my-app__main" tabIndex={-1}>
        <Outlet />
      </main>
      <Pied />
    </div>
  );
}

export function LayoutPro() {
  const { peut } = useSession();
  return (
    <div className="my-app">
      <Evitement />
      <BandeauSimulation />
      <EnteteHorizontal liens={filtrerNav([...navPro, ...navProFidelite, ...navProConditions, ...navProCompte], peut)} espace="pro" accueil="/pro" />
      <main id="contenu" className="my-app__main" tabIndex={-1}>
        <div className="my-conteneur"><Outlet /></div>
      </main>
      <Pied />
    </div>
  );
}

const ORDRE_GROUPES: NonNullable<EntreeNav['groupe']>[] = ['Pilotage', 'Commercial', 'Catalogue', 'Finance', 'Logistique', 'Achats', 'Administration'];

export function LayoutAdmin({ children }: { children?: ReactNode }) {
  const { peut, session } = useSession();
  const [ouvert, setOuvert] = useState(false);
  const liens = filtrerNav([...navAdminCommercial, ...navAdminVentes, ...navAdminAccords, ...navAdminDevis, ...navAdminMarges, ...navAdminLogistique, ...navAdminLivraison, ...navAdminSysteme, ...navAdminSauvegarde], peut);
  const groupes = ORDRE_GROUPES.map((g) => ({ g, l: liens.filter((x) => (x.groupe ?? 'Pilotage') === g) })).filter((x) => x.l.length);
  return (
    <div className="my-app">
      <Evitement />
      <BandeauSimulation />
      <header className="my-entete">
        <nav className="my-nav" aria-label="Barre d'administration">
          <Link to="/admin" className="my-nav__marque" aria-label="MYRON — administration"><Logo /></Link>
          <span className="my-badge my-badge--marque"><Icon icon={ShieldCheck} /> Administration{session?.mfaVerifiee ? ' · MFA vérifiée' : ''}</span>
          <button type="button" className="my-btn my-btn--tertiaire my-burger" aria-expanded={ouvert} aria-controls="laterale-admin" onClick={() => setOuvert((o) => !o)}>
            <Icon icon={ouvert ? X : Menu} /> Menu
          </button>
          <div className="my-nav__actions"><MenuCompte espace="admin" /></div>
        </nav>
      </header>
      <div className="my-admin">
        <nav id="laterale-admin" className={`my-admin__laterale${ouvert ? ' ouvert' : ''}`} aria-label="Modules d'administration">
          {groupes.map(({ g, l }) => (
            <div key={g}>
              <p className="my-admin__groupe">{g}</p>
              <ul>
                {l.map((e) => (
                  <li key={e.vers}>
                    <NavLink to={e.vers} end={e.exact} onClick={() => setOuvert(false)}>
                      {e.icone && <Icon icon={e.icone} />} {e.libelle}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
        <main id="contenu" className="my-admin__contenu" tabIndex={-1}>
          {children ?? <Outlet />}
        </main>
      </div>
    </div>
  );
}

/** Coquille minimale (pages d'authentification). */
export function LayoutAuth() {
  return (
    <div className="my-app">
      <Evitement />
      <BandeauSimulation />
      <header className="my-entete">
        <nav className="my-nav" aria-label="Navigation">
          <Link to="/" className="my-nav__marque" aria-label="MYRON — accueil"><Logo /></Link>
          <div className="my-nav__actions"><Link to="/catalogue">Catalogue</Link></div>
        </nav>
      </header>
      <main id="contenu" className="my-app__main" tabIndex={-1}>
        <div className="my-conteneur" style={{ maxWidth: 520 }}><Outlet /></div>
      </main>
    </div>
  );
}
